/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "pubmatic",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to PubMatic",
          "depth": 0,
          "id": "connect-to-pubmatic"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Register a segment",
          "depth": 1,
          "id": "register-a-segment"
        }, {
          "value": "Audience (multiple identifiers)",
          "depth": 1,
          "id": "audience-multiple-identifiers"
        }, {
          "value": "Hashed email (HEM) audience",
          "depth": 1,
          "id": "hashed-email-hem-audience"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      code: "code",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {ArrayFanOutTip, MatchBoosterDestination, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["PubMatic syncs upload the users in an audience segment to PubMatic over SFTP so you can target them in advertising campaigns. Each sync registers your segment with PubMatic automatically, then uploads the matching users, so there's no separate taxonomy sync to schedule ahead of time. If your model is missing the identifiers PubMatic matches on, you can add emails, mobile ad IDs, and IP addresses with ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#audience-multiple-identifiers",
                children: _jsx(_components.strong, {
                  children: "Audience (multiple identifiers)"
                })
              })
            }), _jsx(_components.td, {
              children: "Add users to and remove them from a PubMatic segment, keyed on hashed emails, mobile ad IDs, IP addresses, or partner IDs such as cookies and RampIDs."
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.pubmatic.com/publisher/docs/data-partner-integration-guide-for-audiences",
                children: "Upload audience data files ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#hashed-email-hem-audience",
                children: _jsx(_components.strong, {
                  children: "Hashed email (HEM) audience"
                })
              })
            }), _jsx(_components.td, {
              children: "Add users to and remove them from a PubMatic segment, keyed on hashed emails."
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://help.pubmatic.com/publisher/docs/data-partner-integration-guide-for-audiences",
                children: "Upload hashed email files ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Both sync types send only changes: each run uploads the users added to or removed from your query result since the last run, as separate ", _jsx(_components.code, {
          children: "addseg"
        }), " and ", _jsx(_components.code, {
          children: "removeseg"
        }), " files. For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-pubmatic",
        children: "Connect to PubMatic"
      }), "\n", _jsx(_components.p, {
        children: "To connect PubMatic, you need an SFTP account and a Data Partner ID (DPID). Your PubMatic Account Manager provides both during onboarding, in an email that also includes the SFTP hostname and the directories to upload to."
      }), "\n", _jsx(_components.p, {
        children: "Enter these credentials when you create the destination:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "SFTP Host"
          }), ": the regional PubMatic SFTP hostname from your onboarding email. Use the region closest to your user base for fastest ingestion."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "SFTP Port"
          }), ": defaults to ", _jsx(_components.code, {
            children: "22"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), " and ", _jsx(_components.strong, {
            children: "Password"
          }), ": your PubMatic SFTP credentials."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data Partner ID (DPID)"
          }), ": your PubMatic-assigned data partner ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Data File Path"
          }), ": the directory where audience data files should land, typically ", _jsx(_components.code, {
            children: "Reports"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Taxonomy File Path"
          }), ": the directory where segment registration files should land, typically ", _jsx(_components.code, {
            children: "taxonomy"
          }), ". It must be different from the Data File Path, because PubMatic separates the two file types by folder."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "HEM Data File Path"
          }), ": the directory where hashed email files should land, typically ", _jsx(_components.code, {
            children: "hem"
          }), ". PubMatic requires hashed emails in their own directory. Fill this in if you send hashed emails with either sync type."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["PubMatic chroots your SFTP user, so enter each path relative to your account's\nhome directory. Use a short name like ", _jsx(_components.code, {
            children: "Reports"
          }), " or ", _jsx(_components.code, {
            children: "taxonomy"
          }), ", not a full\nserver path like ", _jsx(_components.code, {
            children: "/home/your-username/Reports/"
          }), "."]
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Contact your PubMatic Account Manager before sending a segment for the first\ntime. PubMatic typically reviews the initial submission to confirm segment\nIDs, naming, and pricing before activating the data feed."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Pick the sync type by the identifiers you send. Use ", _jsx(_components.strong, {
          children: "Audience (multiple identifiers)"
        }), " for mobile ad IDs, IP addresses, partner IDs, or several identifier types in one sync, and to use Match Booster. Use ", _jsx(_components.strong, {
          children: "Hashed email (HEM) audience"
        }), " when you send only hashed emails and some are MD5 hashes, because Audience (multiple identifiers) sends SHA-256 hashes only."]
      }), "\n", _jsx(_components.p, {
        children: "Both sync types register your segment with PubMatic, then upload the matching users. You configure the segment on the sync itself, so each sync targets a single segment."
      }), "\n", _jsx(_components.h3, {
        id: "register-a-segment",
        children: "Register a segment"
      }), "\n", _jsx(_components.p, {
        children: "PubMatic ingests user data only for segments that already exist in its taxonomy. Both sync types register their segment from the fields below before uploading any users, so a new segment always exists before its data arrives."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch re-registers the segment on the first sync run, on a full resync, and whenever you change any of these fields. That way edits such as a rename, a new price, or a new segment ID reach PubMatic on the next run without a separate step."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment ID"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsxs(_components.td, {
              children: ["The segment ID registered in PubMatic. Hightouch uses it to register the segment and as the ", _jsx(_components.code, {
                children: "segid"
              }), " value on every uploaded row."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment name"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "A human-readable name for the segment."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segment status"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "NEW"
              }), " activates the segment; ", _jsx(_components.code, {
                children: "EMPTY"
              }), " deactivates it. Use ", _jsx(_components.code, {
                children: "NEW"
              }), " when registering a segment. Defaults to ", _jsx(_components.code, {
                children: "NEW"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Description"
              })
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "A description for the segment."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Price (CPM in USD)"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsxs(_components.td, {
              children: ["The CPM price PubMatic requires for the segment, for example ", _jsx(_components.code, {
                children: "2.0000"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Creation date"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsxs(_components.td, {
              children: ["When the segment was created, in UTC. Use the format ", _jsx(_components.code, {
                children: "YYYY-MM-DD HH:MM"
              }), ", for example ", _jsx(_components.code, {
                children: "2026-06-17 14:30"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "audience-multiple-identifiers",
        children: "Audience (multiple identifiers)"
      }), "\n", _jsxs(_components.p, {
        children: ["Audience (multiple identifiers) syncs upload one segment's users keyed on any mix of identifiers. Each run uploads one gzipped TSV file per identifier type: hashed emails go to your ", _jsx(_components.strong, {
          children: "HEM Data File Path"
        }), ", and every other type goes to your ", _jsx(_components.strong, {
          children: "Data File Path"
        }), " with its PubMatic UID type in the last column. This sync type replaces the Audience data sync type, which sent one identifier type per sync and is no longer offered for new syncs."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Map each identifier column to one of the identifier types below. Hightouch checks and normalizes every value before it uploads the value:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Identifier type"
            }), _jsx(_components.th, {
              children: "What Hightouch sends"
            }), _jsx(_components.th, {
              children: "PubMatic UID type"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Email (SHA-256)"
              })
            }), _jsx(_components.td, {
              children: "Plaintext emails, lowercased, trimmed, and SHA-256 hashed. Values already hashed with SHA-256 go out as is; any other hash is dropped."
            }), _jsxs(_components.td, {
              children: ["Set by ", _jsx(_components.strong, {
                children: "Target UID type"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Mobile ad ID (IDFA or AAID)"
              }), ", ", _jsx(_components.strong, {
                children: "Android advertising ID (AAID)"
              }), ", ", _jsx(_components.strong, {
                children: "Apple advertising ID (IDFA)"
              })]
            }), _jsx(_components.td, {
              children: "IDs in UUID format. An all-zero ID is dropped."
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "1"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IPv4 address"
              })
            }), _jsx(_components.td, {
              children: "IPv4 addresses. IPv6 addresses are dropped."
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "45"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cookie"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "0"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "UID 1.0 by TTD"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "3"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "ID5"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "4"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "RampID by LiveRamp"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "6"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Shared ID"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "7"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Panorama ID by Lotame"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "15"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Intent IQ"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "37"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom partner ID"
              })
            }), _jsx(_components.td, {
              children: "The value as is"
            }), _jsxs(_components.td, {
              children: ["Your ", _jsx(_components.strong, {
                children: "Custom UID type"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Partner IDs keep their exact value. The only exception is a value containing a tab or line break, which would split a row of the file, so Hightouch drops it. A row with no identifier in a usable format is rejected."
      }), "\n", _jsxs(_components.p, {
        children: ["Hashed emails require a ", _jsx(_components.strong, {
          children: "HEM Data File Path"
        }), " on the destination. A sync that maps an email without one fails before it uploads anything. PubMatic supports hashed emails in the U.S. region only."]
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "PubMatic"
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "PubMatic"
      }), "\n", _jsx(_components.p, {
        children: "Match Booster can boost the email, mobile ad ID, and IPv4 address identifier types. Partner IDs can't be boosted."
      }), "\n", _jsx(_components.h4, {
        id: "configure-a-custom-partner-id",
        children: "Configure a custom partner ID"
      }), "\n", _jsxs(_components.p, {
        children: ["Map ", _jsx(_components.strong, {
          children: "Custom partner ID"
        }), " when PubMatic assigned your identity partner a UID type that isn't in the list above. Enter that number in ", _jsx(_components.strong, {
          children: "Custom UID type"
        }), ", for example ", _jsx(_components.code, {
          children: "52"
        }), ". A sync that maps a custom partner ID without a numeric ", _jsx(_components.strong, {
          children: "Custom UID type"
        }), " fails before it uploads anything."]
      }), "\n", _jsx(_components.p, {
        children: "Each sync sends one custom UID type. To send IDs from a second custom partner, create another sync."
      }), "\n", _jsx(_components.h4, {
        id: "choose-target-identifiers",
        children: "Choose target identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Target UID type"
        }), " to tell PubMatic which identifiers to resolve hashed emails into: ", _jsx(_components.strong, {
          children: "Cookie"
        }), ", ", _jsx(_components.strong, {
          children: "MAID (IDFA/AAID)"
        }), ", or ", _jsx(_components.strong, {
          children: "Cookie and MAID"
        }), ". It applies only to hashed emails. Leave it empty to use the default target configured with your PubMatic Account Manager at onboarding."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["PubMatic needs a country on every row. Map a column with the user's country to ", _jsx(_components.strong, {
          children: "Country"
        }), ", or map a static value such as ", _jsx(_components.code, {
          children: "US"
        }), ". A sync without a ", _jsx(_components.strong, {
          children: "Country"
        }), " mapping fails before it uploads anything."]
      }), "\n", _jsx(_components.p, {
        children: "There's no separate IP address field. An IPv4 address you map as an identifier fills the IP column of its own file."
      }), "\n", _jsx(_components.h3, {
        id: "hashed-email-hem-audience",
        children: "Hashed email (HEM) audience"
      }), "\n", _jsxs(_components.p, {
        children: ["Hashed email audience syncs upload users keyed on a hashed email address instead of an advertising identifier. PubMatic resolves the hashed emails into the identifiers you target. Each run uploads a gzipped TSV file to your ", _jsx(_components.strong, {
          children: "HEM Data File Path"
        }), "."]
      }), "\n", _jsx(Info, {
        children: "PubMatic supports hashed email activation in the U.S. region only."
      }), "\n", _jsx(_components.h4, {
        id: "configure-email-hashing",
        children: "Configure email hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["Use the hashing options to tell Hightouch whether your mapped ", _jsx(_components.strong, {
          children: "Email"
        }), " column is already hashed:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "If the column holds raw email addresses, Hightouch normalizes each address and hashes it for you."
        }), "\n", _jsx(_components.li, {
          children: "If the column is already hashed, Hightouch sends the values as is."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Then set ", _jsx(_components.strong, {
          children: "Which algorithm are your emails hashed with?"
        }), " to ", _jsx(_components.strong, {
          children: "SHA-256"
        }), " or ", _jsx(_components.strong, {
          children: "MD5"
        }), ". This sets the algorithm Hightouch uses when it hashes raw emails, and tells PubMatic which hash type you're sending."]
      }), "\n", _jsx(_components.h4, {
        id: "choose-target-identifiers-1",
        children: "Choose target identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Target UID type"
        }), " to tell PubMatic which identifiers to resolve the emails into: ", _jsx(_components.strong, {
          children: "Cookie"
        }), ", ", _jsx(_components.strong, {
          children: "MAID (IDFA/AAID)"
        }), ", or ", _jsx(_components.strong, {
          children: "Cookie and MAID"
        }), ". This field is optional. Leave it empty to use the default target configured with your PubMatic Account Manager at onboarding."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mappings-1",
        children: "Field mappings"
      }), "\n", _jsx(_components.p, {
        children: "Map your source columns to PubMatic fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Destination field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Email"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "A raw email address (Hightouch hashes it) or a column that is already hashed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IP Address"
              })
            }), _jsx(_components.td, {
              children: "Conditional"
            }), _jsxs(_components.td, {
              children: ["Provide either ", _jsx(_components.strong, {
                children: "IP Address"
              }), " or ", _jsx(_components.strong, {
                children: "Country"
              }), " (or both) for geo information."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Country"
              })
            }), _jsx(_components.td, {
              children: "Conditional"
            }), _jsxs(_components.td, {
              children: ["Provide either ", _jsx(_components.strong, {
                children: "Country"
              }), " or ", _jsx(_components.strong, {
                children: "IP Address"
              }), " (or both) for geo information."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to PubMatic, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nPubMatic"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch doesn't remove a boosted identifier that drops out of a user's Match Booster enrichment while the user stays in the segment. When a user stays in the segment, Hightouch removes only the identifiers that leave your model's mapped columns. Ask your PubMatic Account Manager how long PubMatic keeps segment membership from SFTP uploads."
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, verify the upload in PubMatic:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Check that the files landed in the configured directories using your SFTP client. Hashed emails land in the HEM Data File Path, every other identifier type in the Data File Path, and the segment registration file in the Taxonomy File Path. Audience (multiple identifiers) syncs write one file per identifier type, with the PubMatic UID type in the last column of each row."
        }), "\n", _jsxs(_components.li, {
          children: ["Hightouch uploads files with a ", _jsx(_components.code, {
            children: ".UPLOADING"
          }), " suffix and renames them once the transfer completes, so only fully renamed files should appear."]
        }), "\n", _jsxs(_components.li, {
          children: ["Ask your PubMatic Account Manager to confirm that PubMatic ingested the files. Data files reference a segment by its ", _jsx(_components.code, {
            children: "segid"
          }), ", so PubMatic drops data for any segment it hasn't finished registering."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
